import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  isValidVideoFormat,
  isValidVideoSize,
  formatFileSize,
  getVideoDuration,
  formatDuration
} from '../../utils/videoUtils'

interface FormVideoUploadProps {
  label: string
  name: string
  value: File | null
  onChange: (file: File | null) => void
  error?: string
  touched?: boolean
  required?: boolean
  disabled?: boolean
  maxSize?: number // in MB (default 500MB)
  helpText?: string
  uploadProgress?: number
  isUploading?: boolean
}

export const FormVideoUpload: React.FC<FormVideoUploadProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  touched,
  required = false,
  disabled = false,
  maxSize = 500, // 500MB default
  helpText,
  uploadProgress = 0,
  isUploading = false
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasError = touched && error

  const handleFileSelect = async (file: File | null) => {
    if (!file) {
      onChange(null)
      setVideoDuration(null)
      setValidationError(null)
      return
    }

    // Validate format
    if (!isValidVideoFormat(file)) {
      setValidationError('Invalid video format. Supported formats: MP4, WebM, OGG, MOV')
      onChange(null)
      return
    }

    // Validate size
    if (!isValidVideoSize(file, maxSize)) {
      setValidationError(`Video size exceeds ${maxSize}MB limit`)
      onChange(null)
      return
    }

    // Get video duration
    try {
      const duration = await getVideoDuration(file)
      setVideoDuration(duration)
    } catch (err) {
      setVideoDuration(null)
    }

    setValidationError(null)
    onChange(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  return (
    <div className="w-full">
      <label className={`block text-sm font-medium mb-1 transition-colors duration-200 ${
        hasError ? 'text-red-600' : 'text-gray-700'
      }`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
          ${isDragging ? 'border-blue-500 bg-blue-50' : ''}
          ${hasError || validationError ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
        `}
      >
        <input
          ref={fileInputRef}
          id={name}
          name={name}
          type="file"
          accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogg,.mov"
          onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
          disabled={disabled}
          className="sr-only"
          aria-invalid={hasError || !!validationError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : helpText ? `${name}-help` : undefined}
        />

        {!value && !isUploading && (
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm-4 27V17l11 7-11 7z"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Click to upload video
            </button>
            <p className="mt-1 text-xs text-gray-500">
              or drag and drop
            </p>
            <p className="mt-2 text-xs text-gray-400">
              MP4, WebM, OGG or MOV up to {maxSize}MB
            </p>
          </div>
        )}

        {/* Upload progress */}
        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              <div className="mt-2 inline-block">
                <svg className="h-8 w-8 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className="bg-blue-600 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{uploadProgress}%</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* File selected display */}
        <AnimatePresence>
          {value && !isUploading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center"
            >
              <div className="mt-2 inline-flex items-center justify-center h-12 w-12 rounded-lg bg-blue-100">
                <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M16 4v12a4 4 0 11-8 0V5h.5a1.5 1.5 0 110 3H8V5h4a2 2 0 110 4H8a4 4 0 11-8 0v12a4 4 0 11-8 0V5h.5a1.5 1.5 0 110 3H0V5h8V4h8z" />
                </svg>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {value.name}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatFileSize(value.size)}
                {videoDuration && (
                  <> • {formatDuration(videoDuration)}</>
                )}
              </p>
              <button
                type="button"
                onClick={() => {
                  onChange(null)
                  setVideoDuration(null)
                }}
                className="mt-2 text-xs text-red-600 hover:text-red-500 font-medium"
              >
                Remove video
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error messages */}
      <AnimatePresence>
        {(hasError || validationError) && (
          <motion.p
            id={`${name}-error`}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="mt-1 text-sm text-red-600"
          >
            {error || validationError}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Help text */}
      {helpText && !error && !validationError && (
        <p id={`${name}-help`} className="mt-1 text-xs text-gray-500">
          {helpText}
        </p>
      )}
    </div>
  )
}

export default FormVideoUpload
